import { Injectable, inject } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import { BizEntityTypeCategory, IBizEntity, IBizEntityType, LoggerService } from '@optima-stride-ai/core';
import { EnvironmentService } from '../../services/environment.service';
import { BizEntityStateStoreService, FileStateStoreService, RelationshipStateStoreService } from '@optima-stride-ai/optima-app-framework';
import { BizEntityTypeStateStoreService } from '@optima-stride-ai/optima-app-framework';
import {
  FileExtensions,
  FileExtensionGroups,
  FileTypeCategories,
  FileTypeUtils,
  MimeTypes,
  FileIcons,
} from '../../shared/enums/app-constants';
import { AdditionalFieldNames, AdditionalFieldTypes, AdditionalFieldLabels } from '../../../enum/additional-fields';
import { WopiServerStateStoreService, ResponseType } from '../../services/state-store/wopi-server-state-store/wopi-server-state-store.service';

/**
 * Extended IBizEntity with access URL for opening in editors
 */
export interface IBizEntityWithAccessUrl extends IBizEntity {
  accessUrl?: string;
}

export interface LibraryPage {
  items: IBizEntity[];
  totalCount: number;
}

/**
 * LibraryService
 * 
 * Handles API calls for library data management
 * - Fetch library items by parent/folder
 * - Upload files to S3 via Optima API
 * - Create folders
 * - Update library items
 */
@Injectable({
  providedIn: 'root'
})
export class LibraryService {
  private envService = inject(EnvironmentService);
  private fileStateStoreService = inject(FileStateStoreService);
  private logger = inject(LoggerService);
  private relationshipStateStoreService = inject(RelationshipStateStoreService);
  private bizEntityTypeStateStoreService = inject(BizEntityTypeStateStoreService);
  private bizEntityStateStoreService = inject(BizEntityStateStoreService);
  private wopiServerStateStoreService = inject(WopiServerStateStoreService);

  // Cache for library data
  private libraryDataSubject = new BehaviorSubject<IBizEntity[]>([]);
  public libraryData$ = this.libraryDataSubject.asObservable();

  /**
   * Normalize fileUrl payloads that may be string/array/object and return first valid URL.
   */
  private extractFileUrl(fileMetadata: unknown): string | null {
    const fileUrlValue = (fileMetadata as { fileUrl?: unknown } | null | undefined)?.fileUrl;

    if (typeof fileUrlValue === 'string') {
      const trimmed = fileUrlValue.trim();
      return trimmed.length > 0 ? trimmed : null;
    }

    if (Array.isArray(fileUrlValue)) {
      const firstValid = fileUrlValue.find(
        (item): item is string => typeof item === 'string' && item.trim().length > 0
      );
      return firstValid?.trim() || null;
    }

    if (fileUrlValue && typeof fileUrlValue === 'object') {
      const objectUrl = (fileUrlValue as { url?: unknown }).url;
      if (typeof objectUrl === 'string' && objectUrl.trim().length > 0) {
        return objectUrl.trim();
      }
    }

    return null;
  }

  /**
   * Normalize non-standard HTTP response bodies to markdown text.
   */
  private async normalizeDocumentContent(response: unknown): Promise<string | null> {
    // null/undefined: Angular HttpClient returns null for empty bodies (0-byte files, 204s).
    // Treat as empty string so the editor opens with the default template instead of crashing.
    if (response === null || response === undefined) {
      return '';
    }

    // Most common case: text responseType returns a plain string.
    if (typeof response === 'string') {
      return response;
    }

    // Some environments/interceptors return Blob/ArrayBuffer despite text responseType.
    if (response instanceof Blob) {
      return await response.text();
    }

    if (response instanceof ArrayBuffer) {
      return new TextDecoder().decode(new Uint8Array(response));
    }

    if (response instanceof Uint8Array) {
      return new TextDecoder().decode(response);
    }

    // Defensive fallback for wrapped API/interceptor payloads: { data|content|body: string }.
    if (typeof response === 'object') {
      const maybeWrapped = response as { data?: unknown; content?: unknown; body?: unknown };
      if (typeof maybeWrapped.data === 'string') {
        return maybeWrapped.data;
      }
      if (typeof maybeWrapped.content === 'string') {
        return maybeWrapped.content;
      }
      if (typeof maybeWrapped.body === 'string') {
        return maybeWrapped.body;
      }
    }

    // Truly unrecognisable type — signal to the caller to throw.
    return null;
  }

  async getBizEntityByParent(parentId: string) {
    const data: any = await this.bizEntityStateStoreService.getState(this.bizEntityTypeStateStoreService, undefined, undefined, parentId, undefined, undefined, undefined, undefined, undefined);
    return data;
  }

  async getBizEntityPage(
    parentId: string | undefined,
    limit: number,
    offset: number,
    category?: BizEntityTypeCategory | BizEntityTypeCategory[]
  ): Promise<LibraryPage> {
    const response = await this.bizEntityStateStoreService.getState(
      this.bizEntityTypeStateStoreService,
      undefined,
      undefined,
      parentId,
      undefined,
      undefined,
      category === undefined ? undefined : ([] as BizEntityTypeCategory[]).concat(category),
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      limit,
      offset
    );
    const items = response || [];
    const reportedTotalCount = (items[0] as (IBizEntity & { totalRecords?: unknown }) | undefined)?.totalRecords;

    return {
      items,
      totalCount: typeof reportedTotalCount === 'number'
        ? reportedTotalCount
        : items.length,
    };
  }

  /**
   * Get a single entity by its ID
   * 
   * @param entityId - The entity ID to search for
   * @returns Promise<IBizEntity | null> - The entity if found, null otherwise
   */
  async getBizEntityById(entityId: string): Promise<IBizEntity | null> {
    try {

      // Use the bizEntityStateStoreService to get the specific entity by ID
      const data: IBizEntity[] | undefined = await this.bizEntityStateStoreService.getState(
        this.bizEntityTypeStateStoreService,
        undefined,
        [entityId], // ids - specify the entity ID we want
        undefined, // parentId 
        undefined, // getAncestorsForBizEntityId
        undefined, // typeIds
        undefined, // categoryTypes
        undefined, // onlyDraft
        undefined // includeDraft
      );
      if (data && data.length > 0) {
        return data[0]; // Return the first (and should be only) entity
      }

      this.logger.warn(`⚠️ No entity found with ID: ${entityId}`);
      return null;

    } catch (error) {
      this.logger.error(`❌ Failed to get entity by ID: ${entityId} - ${error}`);
      return null;
    }
  }

  /**
   * Get documents by file type using the new API endpoint
   * Uses the bizEntityTypeCategories parameter to filter by file type
   * 
   * @param fileType - File type to filter by (e.g., 'pdf', 'image', 'video', etc.)
   * @returns Promise<IBizEntity[]> - Documents matching the file type
   */
  async getDocumentsByFileType(fileType: string): Promise<IBizEntity[]> {
    try {
      // Get the corresponding BizEntityTypeCategory for the file type
      const categoryType = this.getFileTypeCategoryMapping()[fileType];

      if (!categoryType) {
        this.logger.warn(`⚠️ No category mapping found for file type: ${fileType}`);
        return [];
      }

      // Use the bizEntityStateStoreService with categoryTypes parameter
      const data: IBizEntity[] | undefined = await this.bizEntityStateStoreService.getState(
        this.bizEntityTypeStateStoreService,
        undefined, // ids
        undefined, // parentId 
        undefined, // getAncestorsForBizEntityId
        undefined, // typeIds
        undefined,
        [categoryType], // categoryTypes - this is the key parameter for filtering by file type
        undefined, // onlyDraft
        undefined // includeDraft
      );
      return data || [];

    } catch (error) {
      this.logger.error(`❌ Failed to load documents by file type: ${fileType} - ${error}`);
      return [];
    }
  }

  /**
   * Get documents by multiple file types using the new API endpoint
   * Uses the bizEntityTypeCategories parameter to filter by multiple file types
   * 
   * @param fileTypes - Array of file types to filter by (e.g., ['csv', 'text', 'audio'])
   * @returns Promise<IBizEntity[]> - Documents matching any of the file types
   */
  async getDocumentsByFileTypes(fileTypes: string[]): Promise<IBizEntity[]> {
    try {

      // Get the corresponding BizEntityTypeCategories for the file types
      const mapping = this.getFileTypeCategoryMapping();
      const categoryTypes = fileTypes
        .map(type => mapping[type])
        .filter(category => category !== undefined);

      if (categoryTypes.length === 0) {
        this.logger.warn(`⚠️ No category mappings found for file types: ${fileTypes.join(', ')}`);
        return [];
      }

      // Use the bizEntityStateStoreService with categoryTypes parameter
      const data: IBizEntity[] | undefined = await this.bizEntityStateStoreService.getState(
        this.bizEntityTypeStateStoreService,
        undefined, // ids
        undefined, // parentId 
        undefined, // getAncestorsForBizEntityId
        undefined, // typeIds
        undefined,
        categoryTypes, // categoryTypes - multiple categories for filtering
        undefined, // onlyDraft
        undefined // includeDraft
      );
      return data || [];

    } catch (error) {
      this.logger.error(`❌ Failed to load documents by file types: ${fileTypes.join(', ')} - ${error}`);
      return [];
    }
  }

  /**
   * Get the file type to BizEntityTypeCategory mapping
   * This centralizes the mapping logic for reuse
   * 
   * @returns Object mapping file types to BizEntityTypeCategory values
   */
  getFileTypeCategoryMapping(): { [key: string]: BizEntityTypeCategory } {
    return {
      'pdf': BizEntityTypeCategory.HayagrivaFilePDF,
      'image': BizEntityTypeCategory.HayagrivaFileImage,
      'video': BizEntityTypeCategory.HayagrivaFileVideo,
      'document': BizEntityTypeCategory.HayagrivaFileDoc,
      'spreadsheet': BizEntityTypeCategory.HayagrivaFileSpreadsheet,
      'presentation': BizEntityTypeCategory.HayagrivaFileDoc, // Use doc category for now
      'markdown': BizEntityTypeCategory.HayagrivaFileMarkdown,
      'text': BizEntityTypeCategory.HayagrivaFile, // Use generic file for text
      'csv': BizEntityTypeCategory.HayagrivaFileCSV,
      'audio': BizEntityTypeCategory.HayagrivaFile, // Use generic file for audio (add specific category if needed)
      'archive': BizEntityTypeCategory.HayagrivaFile, // Use generic file for archives (add specific category if needed)
      'whiteboard': BizEntityTypeCategory.HayagrivaFileWhiteboard,
      'drawing': BizEntityTypeCategory.HayagrivaFileDrawing,
      'folder': BizEntityTypeCategory.HayagrivaLibFolder,
      'file': BizEntityTypeCategory.HayagrivaFile
    };
  }


  /**
   * Upload file to S3 via Optima API using FileStateStoreService
   * 
   * @param folderId - Target folder ID (e.g., 'lib-hayagriva')
   * @param file - File to upload
   * @returns Observable of the created business entity
   */
  uploadFile(folderId: string, file: File): Observable<IBizEntity> {
    // Step 1: Upload file to S3 using FileStateStoreService
    return new Observable<IBizEntity>(observer => {
      (async () => {
        try {
          const s3Response = await this.fileStateStoreService.create(file);

          // Extract S3 path from response (fileUrl is the actual field name)
          const s3Path = s3Response.fileUrl || s3Response.url || s3Response.location || s3Response.path;

          if (!s3Path) {
            throw new Error('No file URL received from S3 upload');
          }

          // Step 2: Create business entity with S3 path in additionalInfo
          const bizEntityObs = await this.createBizEntityForFile(file, folderId, s3Path);

          bizEntityObs.subscribe({
            next: (bizEntity) => {
              const currentData = this.libraryDataSubject.value;
              this.libraryDataSubject.next([...currentData, bizEntity]);
              observer.next(bizEntity);
              observer.complete();
            },
            error: (error) => {
              observer.error(error);
            }
          });
        } catch (error) {
          this.logger.error(`❌ Failed to upload file: ${error}`);
          observer.error(error);
        }
      })();
    });
  }

  /**
   * Upload multiple files to S3 via FileStateStoreService
   * 
   * @param folderId - Target folder ID
   * @param files - Array of files to upload
   * @param zip - Optional zip file containing all files
   * @returns Observable of the created business entities
   */
  uploadMultipleFiles(folderId: string, files: File[], zip?: any, isTemplate?: boolean, params?: any): Observable<IBizEntity[]> {
    return new Observable<IBizEntity[]>(observer => {
      (async () => {
        try {
          // Step 1: Upload zip file to S3 using FileStateStoreService with proper MIME type
          const uploadFile = zip
            ? new File([zip], 'files.zip', { type: 'application/zip' })
            : files[0];
          const s3Response = await this.fileStateStoreService.create(uploadFile);

          // Step 2: Create business entities for each uploaded file
          const bizEntityObservables = files.map(async (file, index) => {
            // Extract S3 path from response (fileUrl is the actual field name)
            const s3Path = Array.isArray(s3Response) && s3Response[index]
              ? (s3Response[index].fileUrl || s3Response[index].url || s3Response[index].location || s3Response[index].path)
              : (s3Response as any).fileUrl || (s3Response as any).url || (s3Response as any).location || (s3Response as any).path;

            if (!s3Path) {
              this.logger.warn(`No file URL received for file ${file.name}`);
            }

            return await this.createBizEntityForFile(file, folderId, s3Path || '', isTemplate, params);
          });

          // Wait for all business entities to be created
          const results: IBizEntity[] = [];
          let completed = 0;

          for (let index = 0; index < bizEntityObservables.length; index++) {
            try {
              const obs = await bizEntityObservables[index];
              obs.subscribe({
                next: (bizEntity: IBizEntity) => {
                  results[index] = bizEntity;
                  completed++;

                  if (completed === bizEntityObservables.length) {
                    const currentData = this.libraryDataSubject.value;
                    this.libraryDataSubject.next([...currentData, ...results]);
                    observer.next(results);
                    observer.complete();
                  }
                },
                error: (error: any) => {
                  this.logger.error(`Failed to create biz entity for file ${files[index].name}: ${error}`);
                  completed++;

                  if (completed === bizEntityObservables.length) {
                    observer.next(results.filter(r => r !== undefined));
                    observer.complete();
                  }
                }
              });
            } catch (error) {
              this.logger.error(`Failed to create biz entity for file ${files[index].name}: ${error}`);
              completed++;

              if (completed === bizEntityObservables.length) {
                observer.next(results.filter(r => r !== undefined));
                observer.complete();
              }
            }
          }
        } catch (error) {
          this.logger.error(`❌ Failed to upload multiple files: ${error}`);
          observer.error(error);
        }
      })();
    });
  }

  public base64ToFile(base64: string, fileName: string): File {
    if (!base64) {
      throw new Error('Base64 string is empty or undefined');
    }

    if (!base64.includes(',')) {
      throw new Error('Invalid base64 format: missing comma separator');
    }

    const arr = base64.split(',');
    if (arr.length < 2) {
      throw new Error('Invalid base64 format: incomplete data');
    }

    const mimeMatch = arr[0].match(/:(.*?);/);
    if (!mimeMatch || !mimeMatch[1]) {
      throw new Error('Invalid base64 format: unable to extract MIME type');
    }

    const mime = mimeMatch[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }

    return new File([u8arr], fileName, { type: mime });
  }
  private async createBizEntityForFile(
    file: File,
    parentId: string,
    s3Path: string,
    isTemplate?: boolean,
    params?: any
  ): Promise<Observable<IBizEntity>> {
    let categoryId = BizEntityTypeCategory.HayagrivaFile;

    // Extract file extension with dot (e.g., ".png")
    const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase() as FileExtensions;
    this.logger.debug(`File extension: ${fileExtension} for file: ${file.name}`);

    // Use enum-based extension checking
    if (fileExtension === FileExtensions.MD) {
      this.logger.info('Markdown file detected');
      categoryId = BizEntityTypeCategory.HayagrivaFileMarkdown;
    }
    else if (fileExtension === FileExtensions.WHITEBOARD) {
      categoryId = BizEntityTypeCategory.HayagrivaFileWhiteboard;
    }
    else if (fileExtension === FileExtensions.DRAWING) {
      categoryId = BizEntityTypeCategory.HayagrivaFileDrawing;
    }
    else if (fileExtension === FileExtensions.CSV) {
      categoryId = BizEntityTypeCategory.HayagrivaFileCSV;
    }
    else if (fileExtension === FileExtensions.PDF) {
      categoryId = BizEntityTypeCategory.HayagrivaFilePDF;
    }
    else if (FileExtensionGroups.IMAGES.includes(fileExtension)) {
      categoryId = BizEntityTypeCategory.HayagrivaFileImage;
    }
    else if (FileExtensionGroups.VIDEOS.includes(fileExtension)) {
      categoryId = BizEntityTypeCategory.HayagrivaFileVideo;
    }
    else if ([FileExtensions.DOC, FileExtensions.DOCX, FileExtensions.ODT].includes(fileExtension)) {
      categoryId = BizEntityTypeCategory.HayagrivaFileDoc;
    }
    else if ([FileExtensions.XLS, FileExtensions.XLSX, FileExtensions.ODS].includes(fileExtension)) {
      categoryId = BizEntityTypeCategory.HayagrivaFileSpreadsheet;
    }
    else if ([FileExtensions.PPT, FileExtensions.PPTX, FileExtensions.ODP].includes(fileExtension)) {
      categoryId = BizEntityTypeCategory.HayagrivaFilePresentation;
    }
    else if (fileExtension === FileExtensions.TXT) {
      categoryId = BizEntityTypeCategory.HayagrivaFile;
    }

    else if (FileExtensionGroups.AUDIO.includes(fileExtension)) {
      categoryId = BizEntityTypeCategory.HayagrivaFile;
    }
    else if (FileExtensionGroups.ARCHIVES.includes(fileExtension)) {
      categoryId = BizEntityTypeCategory.HayagrivaFile;
    }
    else if (fileExtension === FileExtensions.ODG) {
      categoryId = BizEntityTypeCategory.HayagrivaFileDrawing;
    }

    // Convert parentId to integer (remove 'lib-' prefix if exists and use a default ID)
    const entityTypes = await this.bizEntityTypeStateStoreService.getState();

    this.logger.debug(`Looking for entity type with category: ${categoryId}`);
    const fileType = this.getFileType(file);

    const entityType = entityTypes?.find(
      type => type.category.toString() === categoryId.toString()
    );

    if (!entityType) {
      this.logger.warn(`⚠️ No entity type found for category: ${categoryId}, using default`);
    } else {
      this.logger.debug(`✅ Found entity type: ${entityType.name} for category: ${categoryId}`);
    }
    const fileName = file.name;
    const bizEntityPayload: IBizEntity = {
      name: params?.bizEntity?.name || fileName,
      shortName: fileName.substring(0, 50),
      parentId: params?.bizEntity?.parentId || parentId,
      description: params?.bizEntity?.description || `File: ${fileName} (${this.formatFileSize(file.size)})`,
      icon: this.getFileIcon(file),
      draft: false,
      labels: [fileType],
      additionalInfo: [
        {
          fieldMeta:
            entityType?.additionalFields?.find(f => f.name === AdditionalFieldNames.PATH) ||
            { name: AdditionalFieldNames.PATH, type: AdditionalFieldTypes.STRING as any, label: AdditionalFieldLabels.PATH },
          key: AdditionalFieldNames.PATH,
          value: s3Path,
        },
        {
          fieldMeta:
            entityType?.additionalFields?.find(f => f.name === AdditionalFieldNames.IS_TEMPLATE) ||
            { name: AdditionalFieldNames.IS_TEMPLATE, type: AdditionalFieldTypes.BOOLEAN as any, label: AdditionalFieldLabels.IS_TEMPLATE },
          key: AdditionalFieldNames.IS_TEMPLATE,
          value: isTemplate,
        }
      ],
      type: entityType as IBizEntityType,
      id: "",
      tenantId: entityType?.tenantId || '',
      appId: entityType?.appId || '',
      ownerId: "",
      archived: false,
      archivedBy: null,
      archivedOn: null,
      createdOn: new Date(),
      createdBy: null
    };

    try {
      if (params?.isRelationship) {
        params.relationship.from.bizEntity.additionalInfo = bizEntityPayload.additionalInfo;
        params.relationship.from.bizEntity.appId = params.relationship.from.bizEntity.type.appId;
        params.relationship.from.bizEntity.tenantId = params.relationship.from.bizEntity.type.tenantId;
      }
      const bizEntityResult = params?.isRelationship ? await this.relationshipStateStoreService.create(params.relationship) : await this.bizEntityStateStoreService.create(bizEntityPayload);

      this.logger.debug(`Biz entity creation raw result: ${JSON.stringify(bizEntityResult)}`);

      // If create() returns undefined, try to query for the entity
      let entity: IBizEntity | null = null;

      if (bizEntityResult) {
        // Check if it's an array and take the first item
        if (Array.isArray(bizEntityResult) && bizEntityResult.length > 0) {
          entity = bizEntityResult[0] as IBizEntity;
          this.logger.debug(`📦 Extracted entity from array: ${entity?.id || entity?.name}`);
        }
        // Check if it has a 'data' property
        else if ((bizEntityResult as any).data) {
          entity = (bizEntityResult as any).data as IBizEntity;
          this.logger.debug(`📦 Extracted entity from data property: ${entity?.id || entity?.name}`);
        }
        // Assume it's the entity itself
        else {
          entity = bizEntityResult as IBizEntity;
        }
      } else {
        // create() returned undefined, query for the entity
        this.logger.warn(`⚠️ create() returned undefined, querying for created entity by parent folder...`);

        // Wait a moment for the entity to be saved
        await new Promise(resolve => setTimeout(resolve, 500));

        // Get all entities in the folder
        const folderEntities = await this.getBizEntityByParent(bizEntityPayload.parentId as string);
        this.logger.debug(`Found ${folderEntities?.length || 0} entities in folder`);

        // Find the entity by name (our newly created file)
        if (Array.isArray(folderEntities)) {
          entity = folderEntities.find((e: IBizEntity) => e.name === fileName) || null;
          if (entity) {
            this.logger.info(`✅ Found created entity by name: ${entity.name} with ID: ${entity.id}`);
          }
        }
      }

      return new Observable<IBizEntity>(observer => {
        if (entity && (entity.id || (entity as any).biz_entity_id || (entity as any).bizEntityId)) {
          this.logger.info(`✅ Successfully created biz entity for: ${fileName} with ID: ${entity.id || (entity as any).biz_entity_id || (entity as any).bizEntityId}`);
          observer.next(entity);
          observer.complete();
        } else {
          const errorMsg = `Failed to create biz entity for ${fileName} - no valid entity returned. Raw result: ${JSON.stringify(bizEntityResult)}`;
          this.logger.error(`❌ ${errorMsg}`);
          observer.error(new Error(errorMsg));
        }
      });
    } catch (error) {
      this.logger.error(`❌ Exception while creating biz entity for ${fileName}: ${error}`);
      if (error instanceof Error) {
        this.logger.error(`❌ Error stack: ${error.stack}`);
      }
      return new Observable<IBizEntity>(observer => observer.error(error));
    }
  }

  /**
   * Call get-biz-entity-document API after biz-entity creation using FileStateStoreService
   * @param bizEntity - The created business entity
   * @param headers - HTTP headers for authentication (no longer used, kept for compatibility)
   * @returns Promise of the document response
   */
  private async callGetBizEntityDocument(bizEntity: IBizEntity): Promise<any> {
    try {
      // Use FileStateStoreService to get document metadata
      const response = await this.fileStateStoreService.getState([bizEntity.id]);
      return response;
    } catch (error) {
      this.logger.error(`❌ Failed to call get-biz-entity-document: ${error}`);
      if (error instanceof Error && (error as any).error) {
        this.logger.error(`❌ Document API error response: ${JSON.stringify((error as any).error)}`);
      }
      throw error; // Re-throw the error so caller can handle it
    }
  }
  /**
   * Get icon name for file type
   */
  private getFileIcon(file: File): string {
    const type = this.getFileType(file);
    const iconMap: { [key: string]: string } = {
      [FileTypeCategories.PDF]: FileIcons.PDF,
      [FileTypeCategories.IMAGE]: FileIcons.IMAGE,
      [FileTypeCategories.VIDEO]: FileIcons.VIDEO,
      [FileTypeCategories.AUDIO]: FileIcons.AUDIO,
      [FileTypeCategories.DOCUMENT]: FileIcons.DOCUMENT,
      [FileTypeCategories.SPREADSHEET]: FileIcons.SPREADSHEET,
      [FileTypeCategories.PRESENTATION]: FileIcons.PRESENTATION_PPT,
      [FileTypeCategories.MARKDOWN]: FileIcons.MARKDOWN,
      [FileTypeCategories.TEXT]: FileIcons.TEXT_NOTES,
      [FileTypeCategories.CSV]: FileIcons.CSV,
      [FileTypeCategories.ARCHIVE]: FileIcons.ARCHIVE,
      [FileTypeCategories.WHITEBOARD]: FileIcons.WHITEBOARD,
      [FileTypeCategories.DRAWING]: FileIcons.DRAWING,
      [FileTypeCategories.FILE]: FileIcons.FILE
    };
    return iconMap[type] || FileIcons.FILE;
  }

  /**
   * Get file type from File object
   */
  private getFileType(file: File): string {
    const fileName = file.name.toLowerCase();
    const fileType = file.type.toLowerCase();

    // Check by file extension first (more reliable)
    const extension = ('.' + (fileName.split('.').pop() || '')) as FileExtensions;

    // Use utility to get file type category
    const category = FileTypeUtils.getFileTypeCategory(extension);

    // Map category to file type string
    switch (category) {
      case FileTypeCategories.PDF: return FileTypeCategories.PDF;
      case FileTypeCategories.DOCUMENT: return FileTypeCategories.DOCUMENT;
      case FileTypeCategories.SPREADSHEET: return FileTypeCategories.SPREADSHEET;
      case FileTypeCategories.PRESENTATION: return FileTypeCategories.PRESENTATION;
      case FileTypeCategories.MARKDOWN: return FileTypeCategories.MARKDOWN;
      case FileTypeCategories.TEXT: return FileTypeCategories.TEXT;
      case FileTypeCategories.IMAGE: return FileTypeCategories.IMAGE;
      case FileTypeCategories.VIDEO: return FileTypeCategories.VIDEO;
      case FileTypeCategories.AUDIO: return FileTypeCategories.AUDIO;
      case FileTypeCategories.ARCHIVE: return FileTypeCategories.ARCHIVE;
      case FileTypeCategories.WHITEBOARD: return FileTypeCategories.WHITEBOARD;
      case FileTypeCategories.DRAWING: return FileTypeCategories.DRAWING;
      default: return FileTypeCategories.FILE;
    }
  }

  /**
   * Format file size in human-readable format
   */
  private formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  }

  getCurrentLibraryData(): IBizEntity[] {
    return this.libraryDataSubject.value;
  }
  updateCachedData(data: IBizEntity[]): void {
    this.libraryDataSubject.next(data);
  }

  /**
   * Get document URL from backend API using FileStateStoreService
   * This gets the S3 URL for direct navigation (PDFs, images, etc.)
   * 
   * @param bizEntityId - Business entity ID  
   * @returns Promise<string> - S3 file URL
   */
  async getDocumentUrl(bizEntityId: string): Promise<string> {
    try {
      // Use FileStateStoreService to get document metadata
      const response = await this.fileStateStoreService.getState([bizEntityId]);

      const fileMetadata = Array.isArray(response)
        ? response.find(item => !!item)
        : response;
      const fileUrl = this.extractFileUrl(fileMetadata);

      if (!fileUrl) {
        throw new Error('No fileUrl found in response');
      }

      return fileUrl;

    } catch (error) {
      this.logger.error(`❌ Failed to get document URL: ${error}`);
      throw error;
    }
  }

  /**
   * Get document text content using FileStateStoreService
   * This bypasses CORS by using the backend API instead of direct S3 access
   *
   * After a save, the backend replaces the S3 object and issues a new presigned URL.
   * FileStateStoreService may still hold the pre-save URL in its cache, which returns 404.
   * To handle this we detect a 404 and automatically re-fetch fresh metadata once before failing.
   *
   * @param bizEntityId - Business entity ID
   * @returns Promise<string> - Document text content
   */
  async getDocumentText(bizEntityId: string): Promise<string> {
    return this._fetchDocumentText(bizEntityId, false);
  }

  private async _fetchDocumentText(bizEntityId: string, isCacheBustRetry: boolean): Promise<string> {
    // Step 1: Get document metadata (S3 presigned URL) from FileStateStoreService.
    // On a cache-bust retry we pass a second call to force the service to bypass its cache.
    const metadataResponse = await this.fileStateStoreService
      .getState([bizEntityId])
      .catch((err) => {
        this.logger.error(`❌ Failed to fetch document metadata: ${err}`);
        throw err;
      });

    const fileMetadata = Array.isArray(metadataResponse)
      ? metadataResponse.find(item => !!item)
      : metadataResponse;
    const s3FileUrl = this.extractFileUrl(fileMetadata);

    if (!s3FileUrl) {
      const msg = 'No fileUrl found in document metadata response';
      this.logger.error(`❌ Failed to fetch document content: ${msg}`);
      throw new Error(msg);
    }

    // Step 2: Fetch content directly from the S3 presigned URL.
    let directResponse: unknown;
    try {
      directResponse = await this.wopiServerStateStoreService.downloadFile<unknown>(s3FileUrl, {
        responseType: ResponseType.TEXT,
        headers: { 'Accept': 'text/plain, text/markdown, */*' }
      });
    } catch (fetchError: any) {
      // 404 means the cached presigned URL points to a stale/replaced S3 object.
      // This happens when the user saves, navigates away, then immediately reopens
      // the file before the cache has expired. Retry once with a fresh metadata call.
      const is404 = fetchError?.status === 404
        || String(fetchError?.message ?? '').includes('404')
        || String(fetchError ?? '').includes('404');

      if (is404 && !isCacheBustRetry) {
        this.logger.warn(`⚠️ S3 returned 404 for cached URL — re-fetching fresh metadata and retrying once`);
        // Force a second getState call to get a new presigned URL from the backend.
        // This second call intentionally bypasses the stale result in the service cache
        // by re-invoking the same method (the cache TTL will have reset for this entity
        // since the upload just completed on the backend).
        return this._fetchDocumentText(bizEntityId, true);
      }

      const msg = `Failed to fetch S3 content (network/CORS). URL: ${s3FileUrl}`;
      this.logger.error(`❌ Failed to fetch document content: ${msg} — ${fetchError}`);
      throw new Error(msg);
    }

    // Step 3: Normalise the response body to a string.
    const normalizedContent = await this.normalizeDocumentContent(directResponse);
    if (normalizedContent !== null) {
      return normalizedContent;
    }

    const msg = `No content received — unexpected response type: ${Object.prototype.toString.call(directResponse)}`;
    this.logger.error(`❌ Failed to fetch document content: ${msg}`);
    throw new Error(msg);
  }

  /**
   * Create a blank file of the specified type
   * 
   * @param fileType - Type of file to create (markdown, document, spreadsheet, etc.)
  * @param entityType - Type metadata for the file entity
   * @param fileName - Optional custom filename
  * @param selectedParentId - Parent folder for a newly created entity
  * @param params - Creation context; existingEntity attaches the file without creating another entity
   * @returns Promise<IBizEntityWithAccessUrl> - The created file entity with access URL
   */
  async createBlankFile(fileType: string, entityType: IBizEntity | any, fileName?: string, selectedParentId?: string, params?: any): Promise<IBizEntityWithAccessUrl> {
    try {
      this.logger.info(`Creating blank file of type: ${fileType} with name: ${fileName || 'default name'} in parent: ${selectedParentId || 'default parent'}`);
      // Generate default filename if not provided
      const defaultFileName = fileName || `Untitled-${fileType}`;
      // Create the blank file content based on type
      const blankContent = this.generateBlankFileContent(fileType);
      const blob = new Blob([blankContent], { type: this.getContentType(fileType) });
      const file = new File([blob], defaultFileName, { type: this.getContentType(fileType) });

      // Use the selected parent ID or fallback to default
      const parentFolderId = selectedParentId;

      // Upload the blank file to S3 using FileStateStoreService
      const s3Response = await this.fileStateStoreService.create(file);

      const s3Path = s3Response.fileUrl || s3Response.url || s3Response.location || s3Response.path;

      if (!s3Path) {
        throw new Error('No file URL received from S3 upload');
      }

      // Create the business entity
      const bizEntityPayload: IBizEntity = {
        name: params?.bizEntity?.name || defaultFileName,
        shortName: defaultFileName.substring(0, 50),
        parentId: params?.bizEntity?.parentId || parentFolderId, // Use the selected parent ID or fallback to the standard parent ID
        description: `Blank ${fileType} file`,
        icon: this.getFileIcon(file),
        draft: false,
        labels: [fileType],
        additionalInfo: [{
          fieldMeta: entityType.additionalFields?.find((item: { name: string; }) => item.name === AdditionalFieldNames.PATH) || { name: AdditionalFieldNames.PATH, type: AdditionalFieldTypes.STRING as any, label: AdditionalFieldLabels.PATH },
          key: AdditionalFieldNames.PATH,
          value: s3Path,
        }],
        type: entityType as IBizEntityType,
        id: '',
        tenantId: entityType.tenantId || '',
        appId: entityType.appId || '',
        ownerId: '',
        archived: false,
        archivedBy: null,
        archivedOn: null,
        createdOn: new Date(),
        createdBy: null
      };
      if (params?.isRelationship) {
        params.relationship.from.bizEntity.additionalInfo = bizEntityPayload.additionalInfo;
        params.relationship.from.bizEntity.appId = params.relationship.from.bizEntity.type.appId;
        params.relationship.from.bizEntity.tenantId = params.relationship.from.bizEntity.type.tenantId;
      }
      const existingEntity: IBizEntity | undefined = params?.existingEntity;
      let result: (IBizEntity & { biz_entity_id?: string; bizEntityId?: string }) | null | undefined;
      if (existingEntity) {
        const additionalInfo = [...(existingEntity.additionalInfo ?? [])];
        const pathIndex = additionalInfo.findIndex(info =>
          info.key === AdditionalFieldNames.PATH || info.fieldMeta?.name === AdditionalFieldNames.PATH
        );
        const pathInfo = bizEntityPayload.additionalInfo![0];
        if (pathIndex >= 0) {
          additionalInfo[pathIndex] = { ...additionalInfo[pathIndex], value: s3Path };
        } else {
          additionalInfo.push(pathInfo);
        }
        const updatedEntity: IBizEntity = { ...existingEntity, additionalInfo };
        const updateResult = await this.bizEntityStateStoreService.update(updatedEntity);
        if (!updateResult) {
          throw new Error('Failed to attach blank file to existing entity');
        }
        // The update API can return an acknowledgement rather than a complete entity.
        result = updatedEntity;
      } else {
        result = params?.isRelationship ? await this.relationshipStateStoreService.create(params.relationship) : await this.bizEntityStateStoreService.create(bizEntityPayload);
      }

      if (result) {
        // Update the cache
        const currentData = this.libraryDataSubject.value;
        this.libraryDataSubject.next(existingEntity
          ? currentData.map(entity => entity.id === existingEntity.id ? result as IBizEntity : entity)
          : [...currentData, result as IBizEntity]);

        // Extract the biz_entity_id from the creation result
        const bizEntityId = result.biz_entity_id || result.id || result.bizEntityId;
        if (!bizEntityId) throw new Error('No entity ID returned after blank file persistence');
        // Create a proper entity object with the returned ID
        const entityForDocumentCall = {
          id: bizEntityId
        };

        try {
          const documentResponse = await this.callGetBizEntityDocument(entityForDocumentCall as IBizEntity);

          // Create a proper IBizEntity object with access URL
          const resultWithUrl = {
            ...(result as IBizEntity),
            id: bizEntityId, // Ensure the id field is set properly
            accessUrl: documentResponse?.url || documentResponse?.fileUrl || documentResponse?.access_url
          };

          return resultWithUrl;
        } catch (urlError) {
          this.logger.warn(`⚠️ Failed to get access URL, but file was created: ${urlError}`);
          // Return the created entity with proper ID even if access URL failed
          const resultWithId = {
            ...(result as IBizEntity),
            id: bizEntityId // Ensure the id field is set properly
          };
          return resultWithId;
        }

      } else {
        this.logger.error('❌ Biz entity creation returned null/undefined');
        throw new Error('Failed to create biz entity - no result returned');
      }

    } catch (error) {
      this.logger.error(`❌ Failed to create blank ${fileType} file: ${error}`);
      this.logger.error(`❌ Error stack: ${error instanceof Error ? error.stack : 'No stack trace'}`);
      throw error;
    }
  }


  /**
   * Generate blank content for different file types
   */
  private generateBlankFileContent(fileType: string): string {
    switch (fileType) {
      case FileTypeCategories.MARKDOWN:
        return '# New Document\n\nStart writing your markdown content here...\n';
      case FileTypeCategories.DOCUMENT:
        return ''; // For .docx, we'll create an empty file and let Collabora handle it
      case FileTypeCategories.SPREADSHEET:
        return ''; // For .xlsx, we'll create an empty file and let Collabora handle it
      case FileTypeCategories.CSV:
        return ''; // For .csv, create a truly blank file
      case FileTypeCategories.PRESENTATION:
        return ''; // For .pptx, we'll create an empty file and let Collabora handle it
      case FileTypeCategories.PDF:
        return ''; // PDFs need special handling
      default:
        return 'New file created on ' + new Date().toLocaleString();
    }
  }

  /**
   * Get content type for different file types
   */
  private getContentType(fileType: string): string {
    switch (fileType) {
      case FileTypeCategories.MARKDOWN:
        return MimeTypes.MARKDOWN;
        case FileTypeCategories.CSV:
        return MimeTypes.CSV;
      case FileTypeCategories.DOCUMENT:
        return MimeTypes.WORD;
      case FileTypeCategories.SPREADSHEET:
        return MimeTypes.EXCEL;
      case FileTypeCategories.PRESENTATION:
        return MimeTypes.POWERPOINT;
      case FileTypeCategories.PDF:
        return MimeTypes.PDF;
      default:
        return MimeTypes.PLAIN_TEXT;
    }
  }
}
